import { Lightning, Colors } from '@lightningjs/sdk';
import { Channel, Episode, Media, Show } from 'types/api/media';
import { translate } from 'support/translate';
import Tabs from 'components/common/Tabs/Tabs';
import Tab, { TabTemplateSpec } from 'components/common/Tabs/Tab';
import Grid from 'components/common/Grid';
import { IndexData, LightningBuilder } from 'types/lightning';
import SearchResultTile from 'components/common/tiles/SearchResultTile';
import {
  getSearchTileImage,
  isChannel,
  isEpisode,
  isMovie,
  isSeries,
} from 'support/contentUtils';
import { constants } from 'aliases';
import { SelectItemContext } from 'types/events';
import {
  reportItemSelected,
  reportSearchResultClicked,
} from 'services/analytics/reportingServicePage';
import { PageId } from 'types/pageId';

const NAVBAR_COLLAPSED_WIDTH = constants.sizing.navbar.collapsedWidth;

const GRID_PADDING = 10;

const SEARCH_FILTERS_POSITION = { x: NAVBAR_COLLAPSED_WIDTH, y: 242 };
const RESULTS_FILTER_SPACING = 85;

const SUGGESTED_RESULTS_WRAPPER_POSITION = {
  x: NAVBAR_COLLAPSED_WIDTH,
  y: 329 - GRID_PADDING,
};

const SEARCH_RESULTS_WRAPPER_DIMENSIONS = {
  w: 1564,
  h: 1080 - SUGGESTED_RESULTS_WRAPPER_POSITION.y,
};

const SEARCH_RESULT_TILE_SPACING = { x: 20, y: 64 };
const SEARCH_RESULT_GRID_COLUMNS = 4;

export type SearchResultTabTypes =
  | 'TopResults'
  | 'Series'
  | 'Episodes'
  | 'Movies'
  | 'Channels';

export interface SearchResultsTemplateSpec
  extends Lightning.Component.TemplateSpec {
  SearchFilters: typeof Tabs;
  SearchResultsWrapper: {
    SearchResultsGrid: typeof Grid;
  };
}

export default class SearchResults
  extends Lightning.Component<SearchResultsTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<SearchResultsTemplateSpec>
{
  private _searchData: (Channel | Episode | Show)[] = [];
  private tabType: SearchResultTabTypes = 'TopResults';
  private searchQueryId = '';

  private _SearchFilters = this.getByRef('SearchFilters')!;
  private _SearchResultsWrapper = this.getByRef('SearchResultsWrapper')!;
  private _SearchResultsGrid =
    this._SearchResultsWrapper.getByRef('SearchResultsGrid')!;

  static override _template(): Lightning.Component.Template<SearchResultsTemplateSpec> {
    return {
      SearchFilters: {
        ...SEARCH_FILTERS_POSITION,
        type: Tabs,
        labelStyles: {
          focused: { textColor: Colors('text').get() },
          unfocused: { textColor: Colors('text').alpha(0.5).get() },
          active: { highlight: true },
        },
        passSignals: {
          $onHover: true,
        },
        signals: {
          onIndexChanged: '_onSearchResultsTabChanged',
        },
      },
      SearchResultsWrapper: {
        ...SUGGESTED_RESULTS_WRAPPER_POSITION,
        ...SEARCH_RESULTS_WRAPPER_DIMENSIONS,
        clipping: true,
        SearchResultsGrid: {
          x: GRID_PADDING,
          y: GRID_PADDING,
          w: SEARCH_RESULTS_WRAPPER_DIMENSIONS.w - 2 * GRID_PADDING,
          h: SEARCH_RESULTS_WRAPPER_DIMENSIONS.h - 2 * GRID_PADDING,
          type: Grid,
          spacing: SEARCH_RESULT_TILE_SPACING.y,
          crossSpacing: SEARCH_RESULT_TILE_SPACING.x,
          columns: SEARCH_RESULT_GRID_COLUMNS,
          gcThreshold: 16,
          passSignals: {
            $onHover: true,
          },
        },
      },
    };
  }

  override _setup() {
    this._setState('SearchFilters');
  }

  get searchFilters() {
    return this._SearchFilters;
  }

  get searchResultsGrid() {
    return this._SearchResultsGrid;
  }

  set searchData(data: (Channel | Episode | Show)[]) {
    this._searchData = data;
  }

  clear() {
    this._SearchResultsGrid.clear();

    // Reset the tap type to default
    this.tabType = 'TopResults';
  }

  resetComponent() {
    this._searchData = [];
    this._SearchFilters.patch({ tabs: [], activeTab: null });
  }

  showSearchResults() {
    const tabs = [];
    const topResultsLabel = this.getSearchResultLabel('TopResults');
    const seriesLabel = this.getSearchResultLabel('Series');
    const episodesLabel = this.getSearchResultLabel('Episodes');
    const moviesLabel = this.getSearchResultLabel('Movies');
    const channelsLabel = this.getSearchResultLabel('Channels');

    if (topResultsLabel)
      tabs.push(this.buildTab(topResultsLabel, 'TopResults'));
    if (seriesLabel) tabs.push(this.buildTab(seriesLabel, 'Series'));
    if (episodesLabel) tabs.push(this.buildTab(episodesLabel, 'Episodes'));
    if (moviesLabel) tabs.push(this.buildTab(moviesLabel, 'Movies'));
    if (channelsLabel) tabs.push(this.buildTab(channelsLabel, 'Channels'));

    this._SearchFilters.patch({
      tabs,
      activeTab: tabs.length ? 0 : null,
      spacing: RESULTS_FILTER_SPACING,
    });

    this.showDefaultFiltersAndResults();
  }

  hideResults() {
    this._SearchResultsGrid.clear();
    this._SearchFilters.patch({ visible: false });
    this._SearchResultsWrapper.patch({ visible: false });
  }

  initializeSearchResults(
    searchQueryId?: string,
    tabType?: SearchResultTabTypes,
    currentSearchQueryId?: string,
    currentTabType?: SearchResultTabTypes,
  ) {
    if (searchQueryId) this.searchQueryId = searchQueryId;
    const searchData = this.getCurrentTabData();
    // Do not update the items if the search term or tab has changed
    if (searchQueryId !== currentSearchQueryId || tabType !== currentTabType)
      return;

    const items = this.buildSearchTiles(searchData);
    this._SearchResultsGrid.listType = SearchResultTile;
    this._SearchResultsGrid.patch({ items });
  }

  private _onSearchResultsTabChanged(
    indexData: IndexData,
    searchQueryId: string,
  ) {
    const tabType = this.getSearchTabType(indexData.index);
    if (tabType) this.tabType = tabType;

    this.initializeSearchResults(
      searchQueryId,
      tabType,
      searchQueryId,
      this.tabType,
    );
    this.updateActiveTab();

    return tabType;
  }

  private updateActiveTab() {
    const { index, tabs } = this._SearchFilters;
    if (!tabs.length) return;
    this._SearchFilters.activeTab = index;
  }

  private getSearchTabType(index: number) {
    const availableTabs = this._SearchFilters.tabs;
    return availableTabs[index]?.tabId as SearchResultTabTypes | undefined;
  }

  private hasData() {
    return !!this._SearchResultsGrid.items.length;
  }

  private buildTab(
    label = '',
    tabId?: SearchResultTabTypes,
  ): LightningBuilder<typeof Tab, TabTemplateSpec> {
    return {
      type: Tab,
      label,
      tabId,
    };
  }

  private buildSearchTiles(searchData: (Channel | Episode | Show)[]) {
    return searchData.map(data => {
      return {
        imageUrl: getSearchTileImage(data),
        data,
      };
    });
  }

  private getSearchResultLabel(type: SearchResultTabTypes) {
    const stringKey = `search.tabs.${type}`;
    const numberOfResults = this.getSearchDataByType(type).length;
    return numberOfResults ? translate(stringKey, numberOfResults) : '';
  }

  private getSearchDataByType(type?: SearchResultTabTypes) {
    if (!type || type === 'TopResults') return this._searchData;
    return this._searchData.filter(data => {
      if (type === 'Episodes') {
        return isEpisode(data);
      } else if (type === 'Channels') {
        return isChannel(data);
      } else if (type === 'Movies') {
        return isMovie(data);
      } else if (type === 'Series') {
        return isSeries(data);
      }
    });
  }

  private getCurrentTabData() {
    return this.getSearchDataByType(this.tabType);
  }

  private showDefaultFiltersAndResults() {
    this._SearchFilters.visible = true;
    this._SearchResultsWrapper.visible = true;
  }

  static override _states() {
    return [
      class SearchFilters extends this {
        override _getFocused() {
          return this._SearchFilters;
        }

        override _handleDown() {
          if (!this.hasData()) return;
          this._setState('SearchResultsGrid');
        }
      },
      class SearchResultsGrid extends this {
        override _getFocused() {
          return this._SearchResultsGrid;
        }

        override _handleUp() {
          this._handleBack();
        }

        override _handleBack() {
          this._setState('SearchFilters');
        }
      },
    ];
  }

  $onTileSelected(data: Media, context?: SelectItemContext) {
    this.fireAncestors('$onTileSelected', data, context);

    reportItemSelected(
      {
        imageUrl: context?.imageUrl,
        mainIndex: 0,
        crossIndex: this._SearchResultsGrid.index,
        data,
        rowTitle: this.tabType,
        isTivoTileSource: false,
      },
      PageId.SEARCH,
    );

    reportSearchResultClicked(
      data,
      this.tabType,
      0,
      this._SearchResultsGrid.index,
      this.searchQueryId,
    );
  }
}
